import React, { useState, useEffect, useMemo } from 'react';
import {Card, Row, Col, Button, Image } from 'react-bootstrap';
import api from './api/axiosInstance';
import Select from 'react-select';
import icIndustry from './assets/img/ic-factory.svg';
import icDelete from './assets/img/ic-delete-danger.svg';



const CompanyAddIndustries = ({ industries, onAddIndustry, onRemoveIndustry, onIndustryChange, handleIndustriesSubmit, isEditPage, errors }) => {
    const [isEditing, setIsEditing] = useState(!isEditPage);

    const [industriesData, setIndustriesData] = useState([]);
    const [isLoadingNace, setIsLoadingNace] = useState(true);
    const [naceError, setNaceError] = useState('');

    const industryNameByCode = useMemo(() => {
        const m = new Map();
        (industriesData || []).forEach(ind => m.set(ind.code, ind.name));
        return m;
    }, [industriesData]);

    useEffect(() => {
        let mounted = true;

        const fetchNace = async () => {
            try {
                setIsLoadingNace(true);
                setNaceError('');

                // ✅ uses your configured axios instance
                const res = await api.get('/api/nace');

                if (!mounted) return;

                const data = Array.isArray(res.data) ? res.data : [];
                setIndustriesData(data);
            } catch (err) {
                if (!mounted) return;
                console.log('❌ Failed to load NACE list:', err?.message || err);
                setNaceError('Failed to load industries list.');
                setIndustriesData([]);
            } finally {
                if (!mounted) return;
                setIsLoadingNace(false);
            }
        };

        fetchNace();

        return () => {
            mounted = false;
        };
    }, []);


    const [tempIndustries, setTempIndustries] = useState(JSON.parse(JSON.stringify(industries))); // ✅ Deep copy for restoration

    // ✅ Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempIndustries(JSON.parse(JSON.stringify(industries))); // ✅ Store previous values safely
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (typeof handleIndustriesSubmit === 'function') {
            await handleIndustriesSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
        } else {
            console.error('Error: handleIndustriesSubmit is not a function.');
        }
    };

    // ✅ Cancel editing and restore previous values properly
    const handleCancelClick = () => {
        setIsEditing(false);
        tempIndustries.forEach((industry, index) => {
            onIndustryChange(index, 'industry', industry.industry || '');
            onIndustryChange(index, 'subIndustry', industry.subIndustry || []);
        });
    };

    return (
        <> 
            <Card>
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            {isEditPage && (
                            <div className="float-end">
                                {!isEditing ? (
                                <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                ) : (
                                <>
                                    <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                    <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                </>
                                )}
                            </div>
                            )}

                            <h3><span className="item-number">4.</span>Industries</h3>
                            <hr />

                            {isLoadingNace && (
                            <div className="text-muted small mb-3">Loading industries…</div>
                            )}

                            {!isLoadingNace && naceError && (
                            <div className="text-danger small mb-3">{naceError}</div>
                            )}
                        </Col>
                    </Row>
                    {(industries || []).map((industry, index) => {
                        const selectedIndustry = industriesData.find(ind => ind.code === industry.industry);

                        return (
                            <React.Fragment key={index}>
                                {isEditing ? (
                                    <>
                                        <Card className="mb-4" key={index}>
                                            <Card.Body>
                                                <Row className="mb-3">
                                                    <Col md={12}>
                                                        <label>Industry {index + 1}</label>
                                                        <Select
                                                            options={industriesData.map(ind => ({
                                                                value: ind.code,
                                                                label: ind.name,
                                                            }))}
                                                            value={industry.industry
                                                                ? { value: industry.industry, label: industriesData.find(ind => ind.code === industry.industry)?.name }
                                                                : null
                                                            }
                                                            onChange={(selected) => isEditing && onIndustryChange(index, 'industry', selected?.value || '')}
                                                            isClearable
                                                            isDisabled={!isEditing || isLoadingNace || !!naceError}
                                                            className="react-select"
                                                            classNamePrefix="react-select"
                                                            isInvalid={errors && Array.isArray(errors.industries) && !!errors.industries[index]}
                                                        />
                                                    </Col>
                                                    {errors && Array.isArray(errors.industries) && errors.industries[index] && (
                                                        <div className="text-danger small mt-1">
                                                            {errors.industries[index]}
                                                        </div>
                                                    )}
                                                </Row>
                                                <Row className="mb-3">
                                                    <Col md={12}>
                                                        <label>Sub industry</label>
                                                        <Select
                                                            options={
                                                                selectedIndustry
                                                                ? (selectedIndustry.subIndustries || []).flatMap(sub => [
                                                                    { value: sub.code, label: sub.name },
                                                                    ...(sub.subSubIndustries || []).map(subSub => ({
                                                                        value: subSub.code,
                                                                        label: `- ${subSub.name}`,
                                                                    })),
                                                                    ])
                                                                : []
                                                            }
                                                            value={
                                                                (Array.isArray(industry.subIndustry) ? industry.subIndustry : [])
                                                                    .map(subCode =>
                                                                    (selectedIndustry?.subIndustries || [])
                                                                    .flatMap(sub => [
                                                                        { value: sub.code, label: sub.name },
                                                                        ...(sub.subSubIndustries || []).map(subSub => ({
                                                                            value: subSub.code,
                                                                            label: `- ${subSub.name}`,
                                                                        })),
                                                                    ])
                                                                    .find(option => option.value === subCode)
                                                                )
                                                                .filter(Boolean)
                                                            }
                                                            onChange={(selected) =>
                                                                isEditing &&
                                                                onIndustryChange(
                                                                index,
                                                                'subIndustry',
                                                                Array.isArray(selected) ? selected.map(s => s.value) : [] // ✅ IMPORTANT
                                                                )
                                                            }
                                                            isMulti
                                                            isClearable
                                                            isDisabled={!isEditing || isLoadingNace || !!naceError || !selectedIndustry} // ✅
                                                            className="react-select"
                                                            classNamePrefix="react-select"
                                                        />
                                                        
                                                    </Col>
                                                </Row>
                                                {index > 0 && (
                                                    <Row>
                                                        <Col className="text-end">
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                onClick={() => onRemoveIndustry(index)}
                                                                className="align-center text-danger p-0"
                                                            >
                                                                <Image src={icDelete} alt="Remove" className="me-1" />
                                                                Remove industry
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                    
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </>
                                ) : (
                                    // Not editing
                                    <>
                                        {industry.industry && (
                                            <>
                                                <Row>
                                                    <Col md={12}>
                                                        <Card>
                                                            <Card.Body className="p-3">
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        <Image src={icIndustry} alt="Industry" />
                                                                    </span>
                                                                    <span className="text">
                                                                        Industry:
                                                                        <br />
                                                                        <strong>
                                                                            {industryNameByCode.get(industry.industry) || 'N/A'}
                                                                        </strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                <Row className="sub-card-row">
                                                    <Col md={12}>
                                                        <Card>
                                                            <Card.Body>
                                                                <div className="icon-text">
                                                                    <span className="icon" aria-hidden="true">
                                                                        <Image src={icIndustry} alt="Industry" />
                                                                    </span>
                                                                    <span className="text">
                                                                        Sub industry:
                                                                        <br />
                                                                        <strong>
                                                                            {Array.isArray(industry.subIndustry) && industry.subIndustry.length > 0
                                                                                ? industry.subIndustry
                                                                                    .map(code => {
                                                                                        // Try to find as subIndustry
                                                                                        const sub = (selectedIndustry?.subIndustries || []).find(s => s.code === code);
                                                                                        if (sub) return sub.name;

                                                                                        // Try to find as subSubIndustry
                                                                                        const subSub = selectedIndustry?.subIndustries
                                                                                            ?.flatMap(s => s.subSubIndustries || [])
                                                                                            .find(ss => ss.code === code);
                                                                                        if (subSub) return subSub.name;

                                                                                        return null;
                                                                                    })
                                                                                    .filter(Boolean)
                                                                                    .join(', ')
                                                                                : 'N/A'
                                                                            }
                                                                        </strong>
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </>
                                )}
                            </React.Fragment>
                        );
                    })}
                    {isEditing && (
                        <Row>
                            <Col md={12} className="justify-content-end d-flex">
                                <Button
                                    variant="primary"
                                    onClick={onAddIndustry}
                                    className="btn-sm"
                                    disabled={isLoadingNace || !!naceError} // ✅
                                >
                                    Add a new industry
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

        </>
    );
};

export default CompanyAddIndustries;
